/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { generateOtp } from "./utils/util";
import { Resend } from "./resend";

export type EmbeddedProviderType = 'EMAIL' | 'GOOGLE' | 'TWITTER' | 'PASSKEY';

export interface EmbeddedWalletSession {
    userId: string;
    walletAddress: string;
    provider: EmbeddedProviderType;
    email?: string;
    isActive: boolean;
}

// In-memory store for OTPs (simulating backend redis/db)
const otpStore: Record<string, { code: string; expiresAt: number }> = {};

export class EmbeddedWalletService {
    private static STORAGE_KEY = 'hot_embedded_session';

    /**
     * Sends an OTP to the provided email address
     */
    static async sendOtp(email: string): Promise<boolean> {
        const otp = generateOtp(6);
        const expiresAt = Date.now() + 5 * 60 * 1000; // 5 minutes

        // Store OTP
        otpStore[email] = { code: otp, expiresAt };

        // Send Email via Resend Mock
        const { error } = await Resend.emails.send({
            from: 'auth@thefoundry.app',
            to: email,
            subject: 'Your Login Code for The Foundry',
            html: `
                <div style="font-family: sans-serif; padding: 20px;">
                    <h2>Welcome to The Foundry</h2>
                    <p>Your verification code is:</p>
                    <h1 style="letter-spacing: 5px; background: #f4f4f4; padding: 10px; display: inline-block;">${otp}</h1>
                    <p>This code will expire in 5 minutes.</p>
                </div>
            `
        });

        return !error;
    }

    /**
     * Verifies the OTP for the given email
     */
    static async verifyOtp(email: string, code: string): Promise<boolean> {
        // Simulate network delay
        await new Promise(r => setTimeout(r, 600));

        const record = otpStore[email];
        
        if (!record) return false;
        if (Date.now() > record.expiresAt) {
            delete otpStore[email];
            return false;
        }
        
        if (record.code === code) {
            delete otpStore[email]; // Consume code
            return true;
        }

        return false;
    }

    static async login(provider: EmbeddedProviderType, email?: string, chain: 'SOL' | 'SUI' = 'SOL'): Promise<EmbeddedWalletSession> {
        console.log(`[EmbeddedWallet] Finalizing session for ${provider} on ${chain}...`);
        
        let address = "";
        if (chain === 'SOL') {
            // Generate a mock Base58-like address for Solana
            const alpha = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
            // Create a deterministic-looking address based on email if present for demo continuity
            const seed = email ? email.length : Math.random();
            const suffix = Array.from({ length: 39 }, (_, i) => alpha[Math.floor((seed * (i + 1) * 13) % alpha.length)]).join('');
            address = "Embed" + suffix;
        } else {
            // Generate a mock Hex address for SUI/EVM
            const suffix = Math.random().toString(16).slice(2).repeat(4).slice(0, 63);
            address = `0x${suffix}`;
        }

        const session: EmbeddedWalletSession = {
            userId: `user_${Math.random().toString(36).slice(2)}`,
            walletAddress: address,
            provider,
            email,
            isActive: true
        };

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));
        return session;
    }

    static async logout() {
        localStorage.removeItem(this.STORAGE_KEY);
    }

    static getSession(): EmbeddedWalletSession | null {
        const data = localStorage.getItem(this.STORAGE_KEY);
        return data ? JSON.parse(data) : null;
    }

    static async signMessage(message: string): Promise<string> {
        const session = this.getSession();
        if (!session) throw new Error("No active session");
        
        console.log(`[EmbeddedWallet] Signing message: ${message}`);
        return `0x_mock_signature_${Date.now()}`;
    }

    static async exportPrivateKey(): Promise<string> {
        return "0x_mock_private_key_do_not_share";
    }
}